import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ListItemText,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ChipInput from "material-ui-chip-input";
import axios from "axios";
import moment from "moment";
import grey from "@material-ui/core/colors/grey";
import { Alert } from "@material-ui/lab";
import { saveAs } from "file-saver";
import { useHistory } from "react-router-dom";
import Pagination from "../Pagination";
import ProgrssBar from "../ProgressBar";
import ConfirmDialog from "../ConfirmDialog";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    borderRadius: 14,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#1f2937",
  },
  actionsRow: {
    marginTop: theme.spacing(2),
  },
  tableWrap: {
    maxHeight: 360,
    overflow: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    backgroundColor: "#fff",
  },
  refreshButton: {
    backgroundColor: "#00897b",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#00796b",
    },
  },
  tableHeadCell: {
    backgroundColor: "#3f51b5",
    color: "#fff",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  tableRow: {
    "&:nth-of-type(even)": {
      backgroundColor: "#f8fafc",
    },
    "&:hover": {
      backgroundColor: "#eef4ff",
    },
  },
  emptyCell: {
    padding: theme.spacing(4, 2),
    color: "#6b7280",
  },
  actionCell: {
    whiteSpace: "nowrap",
  },
  subtleText: {
    color: "#475569",
  },
}));

const normalizeConfig = (cfg) => ({
  enabled: cfg?.enabled !== false,
  mode: cfg?.mode || "skip",
  limit: cfg?.limit ?? 3,
  carriers: Array.isArray(cfg?.carriers) ? cfg.carriers : ["ALL"],
  safeNumbers: Array.isArray(cfg?.safeNumbers) ? cfg.safeNumbers : [],
  fallback: cfg?.fallback || "backwash",
  cacheEnabled: cfg?.cacheEnabled === true,
  cacheTtlMs: cfg?.cacheTtlMs || 5000,
});

const normalizeSafeNumber = (value) => {
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 10) return `1${digits}`;
  return digits;
};

const isValidSafeNumber = (value) => /^1\d{10}$/.test(String(value));

const filterInvalidNumbers = (numbers) => ({
  valid: numbers.filter(isValidSafeNumber),
  invalid: numbers.filter((num) => !isValidSafeNumber(num)),
});

const parseCsv = (text) => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim());
  const rows = lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    const row = {};
    headers.forEach((header, idx) => {
      row[header] = values[idx] ?? "";
    });
    return row;
  });
  return { headers, rows };
};

const buildSampleCsv = () => {
  const headers =
    "scope,box,subPort,port,enabled,mode,limit,carriers,safeNumbers,fallback";
  const rows = [
    "box,8012,,,true,skip,3,ALL,12126818191|12126818192,backwash",
    "subport,8012,1,,true,reroute,2,VZW,12126818193|12126818194,backwash",
    "port,8012,1,3,true,reroute,2,ATT,12126818195|12126818196,backwash",
  ];
  return `${headers}\n${rows.join("\n")}\n`;
};

const buildCustomSampleCsv = ({
  scope,
  fromBox,
  toBox,
  fromSubPort,
  toSubPort,
  fromPort,
  toPort,
  mode,
  limit,
  carriers,
  fallback,
  simBoxes,
}) => {
  const headers =
    "scope,box,subPort,port,enabled,mode,limit,carriers,safeNumbers,fallback";

  const startIndex = simBoxes.indexOf(fromBox);
  const endIndex = simBoxes.indexOf(toBox);
  if (startIndex === -1 || endIndex === -1) {
    return `${headers}\n`;
  }

  const boxRange =
    startIndex <= endIndex
      ? simBoxes.slice(startIndex, endIndex + 1)
      : simBoxes.slice(endIndex, startIndex + 1);

  const subPortStart = Math.min(Number(fromSubPort) || 1, Number(toSubPort) || 8);
  const subPortEnd = Math.max(Number(fromSubPort) || 1, Number(toSubPort) || 8);
  const portStart = Math.min(Number(fromPort) || 1, Number(toPort) || 64);
  const portEnd = Math.max(Number(fromPort) || 1, Number(toPort) || 64);
  const carriersValue = Array.isArray(carriers) && carriers.length ? carriers.join("|") : "";
  const limitValue =
    mode && limit !== undefined && limit !== null && limit !== ""
      ? limit
      : "";
  const fallbackValue = mode ? fallback || "" : "";
  const modeValue = mode || "";

  const buildRow = (rowScope, box, subPort = "", port = "") =>
    [
      rowScope,
      box,
      subPort,
      port,
      "true",
      modeValue,
      limitValue,
      carriersValue,
      "",
      fallbackValue,
    ].join(",");

  const rows = [];
  boxRange.forEach((box) => {
    if (scope === "box") {
      rows.push(buildRow("box", box));
      return;
    }

    const allowedSubPorts = getAllowedSubPorts(box).map(Number);
    const subPortList = allowedSubPorts.filter(
      (subPort) => subPort >= subPortStart && subPort <= subPortEnd
    );

    for (const subPort of (subPortList.length ? subPortList : allowedSubPorts)) {
      if (scope === "subport") {
        rows.push(buildRow("subport", box, subPort));
        continue;
      }

      for (let port = portStart; port <= portEnd; port += 1) {
        rows.push(buildRow("port", box, subPort, port));
      }
    }
  });

  return `${headers}\n${rows.join("\n")}\n`;
};

const autoMapHeaders = (headers) => {
  const lower = headers.map((h) => h.toLowerCase());
  const pick = (keys) => {
    const list = Array.isArray(keys) ? keys : [keys];
    for (const key of list) {
      const idx = lower.indexOf(key);
      if (idx >= 0) return headers[idx];
    }
    return "";
  };
  return {
    scope: pick(["scope"]),
    box: pick(["box"]),
    subPort: pick(["subport", "sub_port", "sub-port"]),
    port: pick(["port"]),
    enabled: pick(["enabled"]),
    mode: pick(["mode"]),
    limit: pick(["limit"]),
    carriers: pick(["carriers", "carrier"]),
    safeNumbers: pick(["safenumbers", "safe_numbers", "safe-numbers"]),
    fallback: pick(["fallback"]),
  };
};

const normalizeCsvList = (value) =>
  String(value || "")
    .split(/[|,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeCsvBool = (value) => {
  const text = String(value || "").toLowerCase().trim();
  if (!text) return undefined;
  if (["true", "1", "yes", "y"].includes(text)) return true;
  if (["false", "0", "no", "n"].includes(text)) return false;
  return undefined;
};

const carrierOptions = [
  { value: "ALL", label: "All" },
  { value: "VZW", label: "Verizon" },
  { value: "ATT", label: "AT&T" },
  { value: "TMO", label: "TMobile" },
  { value: "SPRINT", label: "Sprint" },
  { value: "ALLOTHER", label: "ALLOTHER" },
];

const carrierLabelMap = carrierOptions.reduce((map, item) => {
  map[item.value] = item.label;
  return map;
}, {});

const titleCase = (value) => {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const scopeLabelMap = {
  box: "Box",
  subport: "SubPort",
  port: "Port",
};

const modeLabelMap = {
  skip: "Skip",
  reroute: "Reroute",
};

const fallbackLabelMap = {
  backwash: "Backwash",
  dontSend: "DontSend",
};

const eventTypeLabelMap = {
  CSB: "CSB",
  ICR: "ICR",
};

const getEventType = (item) =>
  item?.type || (Number(item?.limit) === 0 ? "ICR" : "CSB");

const formatLimitValue = (limit) => {
  if (limit === "" || limit === null || limit === undefined) return "-";
  return Number(limit) === 0 ? "ICR (0)" : String(limit);
};

const singleSubPortBoxes = ["8012", "8013", "8019"];
const allSubPorts = ["1", "2", "3", "4", "5", "6", "7", "8"];

const getAllowedSubPorts = (box) =>
  singleSubPortBoxes.includes(String(box || "")) ? ["1"] : allSubPorts;

const getConfigKey = (cfg) =>
  cfg?._id ||
  [cfg.scope || "", cfg.box || "", cfg.subPort || "", cfg.port || ""].join("|");

const toCsvValue = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const buildCsvContent = (headers, rows) => {
  const headerLine = headers.map(toCsvValue).join(",");
  const rowLines = rows.map((row) =>
    headers.map((header) => toCsvValue(row[header])).join(",")
  );
  return `${[headerLine, ...rowLines].join("\n")}\n`;
};

const defaultScopeForm = {
  _id: "",
  scope: "box",
  box: "",
  subPort: "",
  port: "",
  enabled: true,
  mode: "",
  limit: "",
  carriers: [],
  safeNumbers: [],
  fallback: "",
};

const normalizeCompareValue = (value) => ({
  ...value,
  carriers: [...(value.carriers || [])],
  safeNumbers: [...(value.safeNumbers || [])],
});

const isSameConfigState = (left, right) =>
  JSON.stringify(normalizeCompareValue(left)) ===
  JSON.stringify(normalizeCompareValue(right));

const CsbManager = () => {
  document.body.style.backgroundColor = grey[300];
  const history = useHistory();
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(0);
  const scopedFormRef = useRef(null);
  const [globalConfig, setGlobalConfig] = useState(normalizeConfig({}));
  const [configs, setConfigs] = useState([]);
  const [selectedConfigs, setSelectedConfigs] = useState([]);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState([]);
  const [eventFilters, setEventFilters] = useState({
    box: "",
    subPort: "",
    port: "",
    action: "",
    type: "",
  });
  const [summaryFilters, setSummaryFilters] = useState({
    box: "",
    subPort: "",
    port: "",
    type: "",
  });
  const [configPage, setConfigPage] = useState({ pageNumber: 1, limit: 24 });
  const [eventPage, setEventPage] = useState({ pageNumber: 1, limit: 24 });
  const [summaryPage, setSummaryPage] = useState({ pageNumber: 1, limit: 24 });
  const [configTotal, setConfigTotal] = useState(0);
  const [eventTotal, setEventTotal] = useState(0);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [simBoxesUnifiedSet, setSimBoxesUnifiedSet] = useState([]);
  const [status, setStatus] = useState(null);
  const [statusType, setStatusType] = useState("success");
  const [configFilters, setConfigFilters] = useState({
    scope: "",
    box: "",
    subPort: "",
    port: "",
  });
  const [editingScope, setEditingScope] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState({
    scope: "",
    box: "",
    subPort: "",
    port: "",
    enabled: "",
    mode: "",
    limit: "",
    carriers: "",
    safeNumbers: "",
    fallback: "",
  });
  const [csvPreview, setCsvPreview] = useState({ configs: [], errors: [] });
  const [csvMode, setCsvMode] = useState("skip");
  const [sampleConfig, setSampleConfig] = useState({
    scope: "box",
    fromBox: "",
    toBox: "",
    fromSubPort: "1",
    toSubPort: "8",
    fromPort: "1",
    toPort: "64",
    mode: "",
    limit: "3",
    carriers: ["ALL"],
    fallback: "backwash",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    content: "",
    onConfirm: null,
  });
  const [scopeForm, setScopeForm] = useState(defaultScopeForm);
  const [initialGlobalConfig, setInitialGlobalConfig] = useState(normalizeConfig({}));
  const [initialScopeForm, setInitialScopeForm] = useState(defaultScopeForm);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [fetchingCount, setFetchingCount] = useState(0);
  const scopeAllowedSubPorts = getAllowedSubPorts(scopeForm.box);
  const sampleFromAllowedSubPorts = getAllowedSubPorts(sampleConfig.fromBox);
  const sampleToAllowedSubPorts = getAllowedSubPorts(sampleConfig.toBox);
  const isGlobalDirty = !isSameConfigState(globalConfig, initialGlobalConfig);
  const isScopeDirty = !isSameConfigState(scopeForm, initialScopeForm);

  const beginFetching = () => {
    setFetchingCount((prev) => prev + 1);
  };

  const endFetching = () => {
    setFetchingCount((prev) => Math.max(0, prev - 1));
  };

  const handleAuthError = useCallback((error) => {
    if (error?.response?.status === 401) {
      window.location.href = "/";
      return true;
    }
    return false;
  }, []);

  const openConfirmDialog = (title, content, onConfirm) => {
    setConfirmDialog({ open: true, title, content, onConfirm });
  };

  const handleCloseConfirm = () => {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  };

  const handleDoConfirm = () => {
    const action = confirmDialog.onConfirm;
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
    if (action) action();
  };

  const fetchGlobal = useCallback(async () => {
    beginFetching();
    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL + "/csb/config/global",
        { withCredentials: true }
      );
      const normalized = normalizeConfig(res.data || {});
      setGlobalConfig(normalized);
      setInitialGlobalConfig(normalized);
    } catch (error) {
      if (!handleAuthError(error)) {
        setStatusType("error");
        setStatus("Failed to load global settings.");
      }
    } finally {
      endFetching();
    }
  }, [handleAuthError]);

  const fetchConfigs = useCallback(async () => {
    const params = {};
    if (configFilters.scope) params.scope = configFilters.scope;
    if (configFilters.box) params.box = configFilters.box;
    if (configFilters.subPort) params.subPort = configFilters.subPort;
    if (configFilters.port) params.port = configFilters.port;
    params.limit = configPage.limit;
    params.skip = (Number(configPage.pageNumber) - 1) * Number(configPage.limit);
    beginFetching();
    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL + "/csb/configs",
        { withCredentials: true, params }
      );
      const scoped = (res.data.configs || []).filter(
        (cfg) => cfg.scope !== "global"
      );
      setConfigs(scoped);
      setConfigTotal(res.data.total || 0);
      setSelectedConfigs((prev) =>
        prev.filter((key) => scoped.some((cfg) => getConfigKey(cfg) === key))
      );
    } catch (error) {
      if (!handleAuthError(error)) {
        setStatusType("error");
        setStatus("Failed to load scoped configs.");
      }
    } finally {
      endFetching();
    }
  }, [
    configFilters.scope,
    configFilters.box,
    configFilters.subPort,
    configFilters.port,
    configPage.pageNumber,
    configPage.limit,
    handleAuthError,
  ]);

  const fetchEvents = useCallback(async () => {
    beginFetching();
    try {
      const params = {
        limit: eventPage.limit,
        skip: (Number(eventPage.pageNumber) - 1) * Number(eventPage.limit),
      };
      if (eventFilters.box) params.box = eventFilters.box;
      if (eventFilters.subPort) params.subPort = eventFilters.subPort;
      if (eventFilters.port) params.port = eventFilters.port;
      if (eventFilters.action) params.action = eventFilters.action;
      if (eventFilters.type) params.type = eventFilters.type;
      const res = await axios.get(
        process.env.REACT_APP_API_URL + "/csb/events",
        { withCredentials: true, params }
      );
      setEvents(res.data.events || []);
      setEventTotal(res.data.total || 0);
    } catch (error) {
      if (!handleAuthError(error)) {
        setStatusType("error");
        setStatus("Failed to load CSB events.");
      }
    } finally {
      endFetching();
    }
  }, [
    eventFilters.box,
    eventFilters.subPort,
    eventFilters.port,
    eventFilters.action,
    eventFilters.type,
    eventPage.pageNumber,
    eventPage.limit,
    handleAuthError,
  ]);

  const fetchSummary = useCallback(async () => {
    beginFetching();
    try {
      const params = {
        limit: summaryPage.limit,
        skip: (Number(summaryPage.pageNumber) - 1) * Number(summaryPage.limit),
      };
      if (summaryFilters.box) params.box = summaryFilters.box;
      if (summaryFilters.subPort) params.subPort = summaryFilters.subPort;
      if (summaryFilters.port) params.port = summaryFilters.port;
      if (summaryFilters.type) params.type = summaryFilters.type;
      const res = await axios.get(
        process.env.REACT_APP_API_URL + "/csb/events/summary",
        { withCredentials: true, params }
      );
      setSummary(res.data.summary || []);
      setSummaryTotal(res.data.total || 0);
    } catch (error) {
      if (!handleAuthError(error)) {
        setStatusType("error");
        setStatus("Failed to load CSB summary.");
      }
    } finally {
      endFetching();
    }
  }, [
    summaryFilters.box,
    summaryFilters.subPort,
    summaryFilters.port,
    summaryFilters.type,
    summaryPage.pageNumber,
    summaryPage.limit,
    handleAuthError,
  ]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchGlobal(), fetchConfigs()]);
    setStatus(null);
  }, [fetchGlobal, fetchConfigs]);

  const fetchSimBoxesUnified = useCallback(async () => {
    beginFetching();
    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL + "/gateway/simboxes-unified",
        { withCredentials: true }
      );
      setSimBoxesUnifiedSet(res.data.SimBoxesUnifiedSet || []);
    } catch (error) {
      handleAuthError(error);
    } finally {
      endFetching();
    }
  }, [handleAuthError]);

  useEffect(() => {
    const getData = async () => {
      try {
        const user = localStorage.getItem("user");
        if (!user) {
          history.push("/");
          return;
        }
        await axios.get(process.env.REACT_APP_API_URL + `/users/${user}`, {
          withCredentials: true,
        });
        await Promise.all([refreshAll(), fetchSimBoxesUnified()]);
        setLoaded(true);
      } catch (error) {
        history.push("/");
      }
    };
    getData();
  }, [history, fetchSimBoxesUnified, refreshAll]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (!loaded) return;
    fetchEvents();
  }, [loaded, fetchEvents]);

  useEffect(() => {
    if (!loaded) return;
    fetchSummary();
  }, [loaded, fetchSummary]);

  useEffect(() => {
    if (!status) return undefined;
    const timer = setTimeout(() => {
      setStatus(null);
    }, 4000);
    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!simBoxesUnifiedSet.length) return;
    setSampleConfig((prev) => ({
      ...prev,
      fromBox: prev.fromBox || simBoxesUnifiedSet[0],
      toBox: prev.toBox || simBoxesUnifiedSet[simBoxesUnifiedSet.length - 1],
    }));
  }, [simBoxesUnifiedSet]);

  useEffect(() => {
    if (!scopeAllowedSubPorts.includes(String(scopeForm.subPort || ""))) {
      const nextSubPort = scopeAllowedSubPorts[0] || "";
      setScopeForm((prev) => ({
        ...prev,
        subPort: nextSubPort,
      }));
      if (!editingScope) {
        setInitialScopeForm((prev) => ({
          ...prev,
          subPort: nextSubPort,
        }));
      }
    }
  }, [scopeForm.box, scopeForm.subPort, scopeAllowedSubPorts, editingScope]);

  useEffect(() => {
    if (!sampleFromAllowedSubPorts.includes(String(sampleConfig.fromSubPort || ""))) {
      setSampleConfig((prev) => ({
        ...prev,
        fromSubPort: sampleFromAllowedSubPorts[0] || "1",
      }));
    }
  }, [sampleConfig.fromBox, sampleConfig.fromSubPort, sampleFromAllowedSubPorts]);

  useEffect(() => {
    if (!sampleToAllowedSubPorts.includes(String(sampleConfig.toSubPort || ""))) {
      setSampleConfig((prev) => ({
        ...prev,
        toSubPort: sampleToAllowedSubPorts[0] || "1",
      }));
    }
  }, [sampleConfig.toBox, sampleConfig.toSubPort, sampleToAllowedSubPorts]);

  if (!loaded) {
    return (
      <React.Fragment>
        <ProgrssBar />
        <h1>Please Wait....</h1>
      </React.Fragment>
    );
  }

  const handleSaveGlobal = async () => {
    const runSaveGlobal = async () => {
    if (
      globalConfig.limit === "" ||
      globalConfig.limit === null ||
      globalConfig.limit === undefined
    ) {
      setStatusType("error");
      setStatus("Limit is required for global settings.");
      return;
    }
    if (globalConfig.mode === "reroute" && Number(globalConfig.limit) === 0) {
      setStatusType("error");
      setStatus("ICR (limit 0) is only allowed with Skip mode.");
      return;
    }
    if (!Array.isArray(globalConfig.safeNumbers)) {
      setStatusType("error");
      setStatus("Safe numbers must be a list.");
      return;
    }
    if (!Array.isArray(globalConfig.carriers)) {
      setStatusType("error");
      setStatus("Carriers must be a list.");
      return;
    }
    const { valid, invalid } = filterInvalidNumbers(globalConfig.safeNumbers);
    if (invalid.length) {
      setGlobalConfig((prev) => ({ ...prev, safeNumbers: valid }));
      setStatusType("error");
      setStatus("Removed invalid safe numbers (must be 11 digits starting with 1).");
      return;
    }
    try {
      setLoading(true);
      beginFetching();
      const payload = {
        ...globalConfig,
        limit: Number(globalConfig.limit),
      };
      await axios.put(
        process.env.REACT_APP_API_URL + "/csb/config/global",
        payload,
        { withCredentials: true }
      );
      await fetchGlobal();
      setStatusType("success");
      setStatus("Global settings saved.");
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to save global settings.";
      setStatusType("error");
      setStatus(message);
    } finally {
      setLoading(false);
      endFetching();
    }
    };
    openConfirmDialog(
      "Update Global CSB Settings",
      "Are you sure you want to update global CSB settings?",
      runSaveGlobal
    );
  };

  const handleScopeChange = (field) => (event) => {
    setScopeForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

const individualCarriers = carrierOptions
  .filter((carrier) => carrier.value !== "ALL")
  .map((carrier) => carrier.value);

const normalizeCarrierSelection = (selection) => {
  const next = Array.isArray(selection) ? selection : [selection];
  const withoutAll = next.filter((value) => value !== "ALL");
  if (withoutAll.length === individualCarriers.length) {
    return ["ALL"];
  }
  if (next.includes("ALL")) {
    return ["ALL"];
  }
  return withoutAll;
};

  const handleGlobalCarriersChange = (event) => {
    const value = normalizeCarrierSelection(event.target.value);
    setGlobalConfig((prev) => ({ ...prev, carriers: value }));
  };

  const handleScopeCarriersChange = (event) => {
    const value = normalizeCarrierSelection(event.target.value);
    setScopeForm((prev) => ({ ...prev, carriers: value }));
  };

  const handleSampleCarriersChange = (event) => {
    const value = normalizeCarrierSelection(event.target.value);
    setSampleConfig((prev) => ({ ...prev, carriers: value }));
  };

  const handleAddGlobalSafeNumber = (chip) => {
    const normalized = normalizeSafeNumber(chip);
    if (!isValidSafeNumber(normalized)) {
      setStatusType("error");
      setStatus("Safe number must be 11 digits and start with 1.");
      return;
    }
    setGlobalConfig((prev) => ({
      ...prev,
      safeNumbers: [...prev.safeNumbers, normalized],
    }));
  };

  const handleAddScopedSafeNumber = (chip) => {
    const normalized = normalizeSafeNumber(chip);
    if (!isValidSafeNumber(normalized)) {
      setStatusType("error");
      setStatus("Safe number must be 11 digits and start with 1.");
      return;
    }
    setScopeForm((prev) => ({
      ...prev,
      safeNumbers: [...prev.safeNumbers, normalized],
    }));
  };

  const handleSaveScope = async () => {
    const runSaveScope = async () => {
    if (scopeForm.mode) {
      if (
        scopeForm.limit === "" ||
        scopeForm.limit === null ||
        scopeForm.limit === undefined
      ) {
        setStatusType("error");
        setStatus("Limit is required when mode is set.");
        return;
      }
      if (scopeForm.mode === "reroute" && Number(scopeForm.limit) === 0) {
        setStatusType("error");
        setStatus("ICR (limit 0) is only allowed with Skip mode.");
        return;
      }
      if (!Array.isArray(scopeForm.carriers) || scopeForm.carriers.length === 0) {
        setStatusType("error");
        setStatus("Carriers are required when mode is set.");
        return;
      }
    }
    if (!Array.isArray(scopeForm.safeNumbers)) {
      setStatusType("error");
      setStatus("Safe numbers must be a list.");
      return;
    }
    if (!Array.isArray(scopeForm.carriers)) {
      setStatusType("error");
      setStatus("Carriers must be a list.");
      return;
    }
    const { valid, invalid } = filterInvalidNumbers(scopeForm.safeNumbers);
    if (invalid.length) {
      setScopeForm((prev) => ({ ...prev, safeNumbers: valid }));
      setStatusType("error");
      setStatus("Removed invalid safe numbers (must be 11 digits starting with 1).");
      return;
    }
    const payload = {
      _id: scopeForm._id || undefined,
      scope: scopeForm.scope,
      box: scopeForm.box || undefined,
      subPort: scopeForm.subPort || undefined,
      port: scopeForm.port || undefined,
      enabled: scopeForm.enabled,
      mode: scopeForm.mode || undefined,
      limit:
        scopeForm.limit === "" ||
        scopeForm.limit === null ||
        scopeForm.limit === undefined
          ? undefined
          : Number(scopeForm.limit),
      carriers: scopeForm.carriers,
      safeNumbers: scopeForm.safeNumbers,
      fallback: scopeForm.fallback || undefined,
    };
    try {
      setLoading(true);
      beginFetching();
      await axios.post(
        process.env.REACT_APP_API_URL + "/csb/config",
        payload,
        { withCredentials: true }
      );
      await fetchConfigs();
      setStatusType("success");
      setStatus("Scoped config saved.");
      setEditingScope(null);
      setScopeForm(defaultScopeForm);
      setInitialScopeForm(defaultScopeForm);
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to save scoped config.";
      setStatusType("error");
      setStatus(message);
    } finally {
      setLoading(false);
      endFetching();
    }
    };
    openConfirmDialog(
      editingScope ? "Update Scoped CSB Config" : "Save Scoped CSB Config",
      editingScope
        ? "Are you sure you want to update this scoped CSB config?"
        : "Are you sure you want to save this scoped CSB config?",
      runSaveScope
    );
  };

  const handleDeleteScope = async (cfg) => {
    const runDeleteScope = async () => {
      try {
      beginFetching();
      await axios.delete(process.env.REACT_APP_API_URL + "/csb/config", {
        data: {
          _id: cfg._id,
        },
        withCredentials: true,
      });
      await fetchConfigs();
      setStatusType("success");
      setStatus("Scoped config deleted.");
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to delete scoped config.";
      setStatusType("error");
      setStatus(message);
    } finally {
      endFetching();
    }
    };
    openConfirmDialog(
      "Delete Scoped CSB Config",
      "Are you sure you want to delete this scoped CSB config?",
      runDeleteScope
    );
  };

  const handleToggleConfig = (cfg) => {
    const key = getConfigKey(cfg);
    setSelectedConfigs((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const handleToggleAllConfigs = () => {
    if (selectedConfigs.length === configs.length) {
      setSelectedConfigs([]);
      return;
    }
    setSelectedConfigs(configs.map(getConfigKey));
  };

  const handleDeleteSelectedConfigs = async () => {
    if (!selectedConfigs.length) return;
    const runDeleteSelectedConfigs = async () => {
      try {
      beginFetching();
      const payload = configs
        .filter((cfg) => selectedConfigs.includes(getConfigKey(cfg)))
        .map((cfg) => ({
          _id: cfg._id,
        }));
      await axios.post(
        process.env.REACT_APP_API_URL + "/csb/config/bulk-delete",
        { configs: payload },
        { withCredentials: true }
      );
      setSelectedConfigs([]);
      await fetchConfigs();
      setStatusType("success");
      setStatus("Selected scoped configs deleted.");
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to delete selected scoped configs.";
      setStatusType("error");
      setStatus(message);
    } finally {
      endFetching();
    }
    };
    openConfirmDialog(
      "Delete Selected Scoped CSB Configs",
      `Are you sure you want to delete ${selectedConfigs.length} selected scoped CSB config(s)?`,
      runDeleteSelectedConfigs
    );
  };

  const handleDeleteAllScopedConfigs = () => {
    const scopeLabel = [
      configFilters.scope && `scope=${configFilters.scope}`,
      configFilters.box && `box=${configFilters.box}`,
      configFilters.subPort && `subPort=${configFilters.subPort}`,
      configFilters.port && `port=${configFilters.port}`,
    ]
      .filter(Boolean)
      .join(", ");

    openConfirmDialog(
      "Delete All Scoped CSB Configs",
      scopeLabel
        ? `Are you sure you want to delete all scoped CSB configs matching: ${scopeLabel}?`
        : "Are you sure you want to delete all scoped CSB configs?",
      async () => {
        try {
          beginFetching();
          const res = await axios.post(
            process.env.REACT_APP_API_URL + "/csb/config/delete-all",
            {
              scope: configFilters.scope || undefined,
              box: configFilters.box || undefined,
              subPort: configFilters.subPort || undefined,
              port: configFilters.port || undefined,
            },
            { withCredentials: true }
          );

          setSelectedConfigs([]);
          setConfigPage({ pageNumber: 1, limit: configPage.limit });
          await fetchConfigs();
          setStatusType("success");
          setStatus(
            res.data.deletedCount
              ? `Deleted ${res.data.deletedCount} scoped config(s).`
              : "No scoped configs found to delete."
          );
        } catch (err) {
          const message =
            err?.response?.data?.error || "Failed to delete scoped configs.";
          setStatusType("error");
          setStatus(message);
        } finally {
          endFetching();
        }
      }
    );
  };

  const handleFilterChange = (field) => (event) => {
    setConfigPage((prev) => ({ ...prev, pageNumber: 1 }));
    setConfigFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleConfigPageChange = (pageNumber) => {
    setConfigPage((prev) => ({ ...prev, pageNumber }));
  };

  const handleConfigLimitChange = (limit) => {
    setConfigPage({ pageNumber: 1, limit: Number(limit) });
  };

  const handleEventFilterChange = (field) => (event) => {
    setEventPage((prev) => ({ ...prev, pageNumber: 1 }));
    setEventFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSummaryFilterChange = (field) => (event) => {
    setSummaryPage((prev) => ({ ...prev, pageNumber: 1 }));
    setSummaryFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEventPageChange = (pageNumber) => {
    setEventPage((prev) => ({ ...prev, pageNumber }));
  };

  const handleEventLimitChange = (limit) => {
    setEventPage({ pageNumber: 1, limit: Number(limit) });
  };

  const handleSummaryPageChange = (pageNumber) => {
    setSummaryPage((prev) => ({ ...prev, pageNumber }));
  };

  const handleSummaryLimitChange = (limit) => {
    setSummaryPage({ pageNumber: 1, limit: Number(limit) });
  };

  const handleEditScope = (cfg) => {
    const nextScopeForm = {
      _id: cfg._id || "",
      scope: cfg.scope || "box",
      box: cfg.box || "",
      subPort: cfg.subPort || "",
      port: cfg.port || "",
      enabled: cfg.enabled !== false,
      mode: cfg.mode || "",
      limit: cfg.limit ?? "",
      carriers: cfg.carriers || [],
      safeNumbers: cfg.safeNumbers || [],
      fallback: cfg.fallback || "",
    };
    setScopeForm(nextScopeForm);
    setInitialScopeForm(nextScopeForm);
    setEditingScope(cfg._id || `${cfg.scope}-${cfg.box}-${cfg.subPort}-${cfg.port}`);
    setTabIndex(0);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 0);
  };

  const handleCancelEdit = () => {
    setEditingScope(null);
    setScopeForm(defaultScopeForm);
    setInitialScopeForm(defaultScopeForm);
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || "");
      const { headers, rows } = parseCsv(content);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setCsvMapping(autoMapHeaders(headers));
      setCsvPreview({ configs: [], errors: [] });
      setStatusType("success");
      setStatus(`Loaded ${rows.length} rows from CSV.`);
    };
    reader.readAsText(file);
  };

  const handleSampleDownload = () => {
    const content = buildSampleCsv();
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "csb_scoped_config_sample.csv");
  };

  const handleCustomSampleChange = (field) => (event) => {
    setSampleConfig((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCustomSampleDownload = () => {
    if (!sampleConfig.fromBox || !sampleConfig.toBox) {
      setStatusType("error");
      setStatus("Select box range for custom sample download.");
      return;
    }
    if (sampleConfig.mode) {
      if (
        sampleConfig.limit === "" ||
        sampleConfig.limit === null ||
        sampleConfig.limit === undefined
      ) {
        setStatusType("error");
        setStatus("Limit is required when custom sample mode is set.");
        return;
      }
      if (sampleConfig.mode === "reroute" && Number(sampleConfig.limit) === 0) {
        setStatusType("error");
        setStatus("ICR (limit 0) is only allowed with Skip mode.");
        return;
      }
      if (!Array.isArray(sampleConfig.carriers) || sampleConfig.carriers.length === 0) {
        setStatusType("error");
        setStatus("Carriers are required when custom sample mode is set.");
        return;
      }
      if (!sampleConfig.fallback) {
        setStatusType("error");
        setStatus("Fallback is required when custom sample mode is set.");
        return;
      }
    }
    const content = buildCustomSampleCsv({
      ...sampleConfig,
      simBoxes: simBoxesUnifiedSet,
    });
    const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "csb_custom_scoped_config_sample.csv");
  };

  const exportEventsCsv = async () => {
    try {
      beginFetching();
      const headers = [
        "Time",
        "Type",
        "Box",
        "SubPort",
        "Port",
        "Carrier",
        "Action",
        "Phone",
        "Reroute",
      ];

      const limit = 500;
      let skip = 0;
      let total = 0;
      let rows = [];
      do {
        const params = { limit, skip };
        if (eventFilters.box) params.box = eventFilters.box;
        if (eventFilters.subPort) params.subPort = eventFilters.subPort;
        if (eventFilters.port) params.port = eventFilters.port;
        if (eventFilters.action) params.action = eventFilters.action;
        if (eventFilters.type) params.type = eventFilters.type;
        const res = await axios.get(
          process.env.REACT_APP_API_URL + "/csb/events",
          { withCredentials: true, params }
        );
        const batch = res.data.events || [];
        total = res.data.total || 0;
        rows = rows.concat(batch);
        skip += batch.length;
        if (!batch.length) break;
      } while (skip < total);

      const csvRows = rows.map((evt) => ({
        Time: evt.createdAt
          ? moment(evt.createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "",
        Type: eventTypeLabelMap[getEventType(evt)] || getEventType(evt) || "",
        Box: evt.box || "",
        SubPort: evt.subPort || "",
        Port: evt.port || "",
        Carrier: evt.carrier || "",
        Action: evt.action || "",
        Phone: evt.phone || "",
        Reroute: evt.reroutePhone || "",
      }));
      const blob = new Blob([buildCsvContent(headers, csvRows)], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blob, "csb_events.csv");
    } catch (error) {
      if (!handleAuthError(error)) {
        setStatusType("error");
        setStatus("Failed to download CSB events.");
      }
    } finally {
      endFetching();
    }
  };

  const exportSummaryCsv = async () => {
    try {
      beginFetching();
      const headers = ["Type", "Box", "SubPort", "Port", "Count"];

      const limit = 500;
      let skip = 0;
      let total = 0;
      let rows = [];
      do {
        const params = { limit, skip };
        if (summaryFilters.box) params.box = summaryFilters.box;
        if (summaryFilters.subPort) params.subPort = summaryFilters.subPort;
        if (summaryFilters.port) params.port = summaryFilters.port;
        if (summaryFilters.type) params.type = summaryFilters.type;
        const res = await axios.get(
          process.env.REACT_APP_API_URL + "/csb/events/summary",
          { withCredentials: true, params }
        );
        const batch = res.data.summary || [];
        total = res.data.total || 0;
        rows = rows.concat(batch);
        skip += batch.length;
        if (!batch.length) break;
      } while (skip < total);

      const csvRows = rows.map((row) => ({
        Type: eventTypeLabelMap[row._id?.type] || row._id?.type || "",
        Box: row._id?.box || "",
        SubPort: row._id?.subPort || "",
        Port: row._id?.port || "",
        Count: row.count || 0,
      }));
      const blob = new Blob([buildCsvContent(headers, csvRows)], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blob, "csb_summary.csv");
    } catch (error) {
      if (!handleAuthError(error)) {
        setStatusType("error");
        setStatus("Failed to download CSB summary.");
      }
    } finally {
      endFetching();
    }
  };

  const handleCsvMappingChange = (field) => (event) => {
    setCsvMapping((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const resetCsvImportState = () => {
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvMapping({
      scope: "",
      box: "",
      subPort: "",
      port: "",
      enabled: "",
      mode: "",
      limit: "",
      carriers: "",
      safeNumbers: "",
      fallback: "",
    });
    setCsvPreview({ configs: [], errors: [] });
    setCsvMode("skip");
  };

  const buildCsvConfigs = () => {
    const errors = [];
    const configs = csvRows.map((row, idx) => {
      const pick = (field) => {
        const header = csvMapping[field];
        return header ? row[header] : "";
      };
      const scopeRaw = pick("scope");
      const box = pick("box");
      const subPort = pick("subPort");
      const port = pick("port");
      const mode = pick("mode");
      const limit = pick("limit");
      const carriers = normalizeCsvList(pick("carriers"));
      const safeNumbers = normalizeCsvList(pick("safeNumbers")).map(
        normalizeSafeNumber
      );
      const fallback = pick("fallback");
      const enabled = normalizeCsvBool(pick("enabled"));

      let scope = scopeRaw ? String(scopeRaw).toLowerCase().trim() : "";
      if (!scope) {
        if (port) scope = "port";
        else if (subPort) scope = "subport";
        else if (box) scope = "box";
      }

      const record = {
        scope,
        box: box ? String(box).trim() : "",
        subPort: subPort ? String(subPort).trim() : "",
        port: port ? String(port).trim() : "",
        enabled,
        mode: mode ? String(mode).toLowerCase().trim() : "",
        limit:
          limit === "" || limit === null || limit === undefined
            ? undefined
            : Number(limit),
        carriers,
        safeNumbers,
        fallback: fallback ? String(fallback).toLowerCase().trim() : "",
      };

      if (!record.scope) {
        errors.push({ row: idx + 1, error: "Missing scope" });
      }
      if (!record.box) {
        errors.push({ row: idx + 1, error: "Missing box" });
      }
      if (record.scope === "subport" && !record.subPort) {
        errors.push({ row: idx + 1, error: "Missing subPort" });
      }
      if (record.scope === "port" && !record.port) {
        errors.push({ row: idx + 1, error: "Missing port" });
      }
      const invalidNumbers = record.safeNumbers.filter(
        (num) => !isValidSafeNumber(num)
      );
      if (invalidNumbers.length) {
        errors.push({ row: idx + 1, error: "Invalid safeNumbers" });
      }

      return record;
    });
    return { configs, errors };
  };

  const handleCsvPreview = () => {
    if (!csvRows.length) {
      setStatusType("error");
      setStatus("Upload a CSV file first.");
      return;
    }
    const { configs, errors } = buildCsvConfigs();
    setCsvPreview({ configs, errors });
    if (errors.length) {
      setStatusType("error");
      setStatus(`Found ${errors.length} validation errors.`);
    } else {
      setStatusType("success");
      setStatus("CSV preview ready.");
    }
  };

  const handleCsvImport = async () => {
    const { configs, errors } = buildCsvConfigs();
    if (errors.length && csvMode === "strict") {
      setStatusType("error");
      setStatus("Fix CSV errors before importing.");
      setCsvPreview({ configs, errors });
      return;
    }
    const runCsvImport = async () => {
      try {
      setLoading(true);
      beginFetching();
      await axios.post(
        process.env.REACT_APP_API_URL + "/csb/config/bulk",
        { configs, mode: csvMode === "strict" ? "strict" : "skip" },
        { withCredentials: true }
      );
      await fetchConfigs();
      setStatusType("success");
      setStatus("CSV import completed.");
      resetCsvImportState();
    } catch (err) {
      const message =
        err?.response?.data?.error || "CSV import failed.";
      setStatusType("error");
      setStatus(message);
    } finally {
      setLoading(false);
      endFetching();
    }
    };
    openConfirmDialog(
      "Import Scoped CSB Configs",
      `Are you sure you want to import ${configs.length} scoped CSB config row(s)?`,
      runCsvImport
    );
  };

  const handleTabChange = (_event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <React.Fragment>
      {fetchingCount > 0 && <ProgrssBar />}
      <Container maxWidth="xl" style={{ marginTop: 24 }}>
      <Paper className={classes.paper} style={{ marginBottom: 16 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="nowrap"
        >
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Config" />
            <Tab label="Import" />
            <Tab label="Events" />
            <Tab label="Summary" />
          </Tabs>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, color: "#475569", whiteSpace: "nowrap", marginLeft: 16 }}
          >
            Carrier Streak Breaker
          </Typography>
        </Box>
      </Paper>
      {status && (
        <Box mb={2}>
          <Alert severity={statusType}>{status}</Alert>
        </Box>
      )}

      {tabIndex === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.paper}>
              <Typography className={classes.sectionTitle}>
                CSB Global Settings
              </Typography>
              <Typography
                variant="body2"
                className={classes.subtleText}
                style={{ marginBottom: 12 }}
              >
                ICR (Immediate Carrier Restriction): Set Mode To Skip And Limit To 0.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={globalConfig.enabled}
                        onChange={(e) =>
                          setGlobalConfig((prev) => ({
                            ...prev,
                            enabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Mode</InputLabel>
                    <Select
                      value={globalConfig.mode}
                      onChange={(e) =>
                        setGlobalConfig((prev) => ({
                          ...prev,
                          mode: e.target.value,
                        }))
                      }
                      label="Mode"
                    >
                      <MenuItem value="skip">Skip</MenuItem>
                      <MenuItem value="reroute">Reroute</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Limit"
                    type="number"
                    fullWidth
                    value={globalConfig.limit}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        limit:
                          e.target.value === ""
                            ? ""
                            : Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Fallback</InputLabel>
                    <Select
                      value={globalConfig.fallback}
                      onChange={(e) =>
                        setGlobalConfig((prev) => ({
                          ...prev,
                          fallback: e.target.value,
                        }))
                      }
                      label="Fallback"
                    >
                      <MenuItem value="backwash">Backwash</MenuItem>
                      <MenuItem value="dontSend">DontSend</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Carriers</InputLabel>
                  <Select
                    multiple
                    value={globalConfig.carriers}
                    onChange={handleGlobalCarriersChange}
                    renderValue={(selected) =>
                      selected.map((value) => carrierLabelMap[value] || value).join(", ")
                    }
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {carrierOptions.map((carrier) => (
                      <MenuItem
                        key={carrier.value}
                        value={carrier.value}
                        disabled={
                          carrier.value !== "ALL" &&
                          globalConfig.carriers.includes("ALL")
                        }
                      >
                        <Checkbox
                          checked={globalConfig.carriers.indexOf(carrier.value) > -1}
                        />
                        <ListItemText primary={carrier.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <ChipInput
                    label="Safe Numbers"
                    fullWidth
                    value={globalConfig.safeNumbers}
                    onAdd={handleAddGlobalSafeNumber}
                    onDelete={(chip, index) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        safeNumbers: prev.safeNumbers.filter((_, i) => i !== index),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={globalConfig.cacheEnabled}
                        onChange={(e) =>
                          setGlobalConfig((prev) => ({
                            ...prev,
                            cacheEnabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Cache Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Cache TTL (ms)"
                    type="number"
                    fullWidth
                    value={globalConfig.cacheTtlMs}
                    onChange={(e) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        cacheTtlMs: Number(e.target.value),
                      }))
                    }
                  />
                </Grid>
              </Grid>
              <Box className={classes.actionsRow}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveGlobal}
                  disabled={loading || !isGlobalDirty}
                >
                  Save Global Settings
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper className={classes.paper} ref={scopedFormRef}>
              <Typography className={classes.sectionTitle}>
                {editingScope ? "Edit Scoped CSB Config" : "Add Scoped CSB Config"}
              </Typography>
              <Typography
                variant="body2"
                className={classes.subtleText}
                style={{ marginBottom: 12 }}
              >
                ICR (Immediate Carrier Restriction): Set Mode To Skip And Limit To 0.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={scopeForm.scope}
                      onChange={handleScopeChange("scope")}
                      label="Scope"
                    >
                      <MenuItem value="box">Box</MenuItem>
                      <MenuItem value="subport">SubPort</MenuItem>
                      <MenuItem value="port">Port</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Box</InputLabel>
                    <Select
                      value={scopeForm.box}
                      onChange={handleScopeChange("box")}
                      label="Box"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {simBoxesUnifiedSet.map((box) => (
                        <MenuItem key={box} value={box}>
                          {box}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {scopeForm.scope !== "box" && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>SubPort</InputLabel>
                        <Select
                          value={scopeForm.subPort}
                          onChange={handleScopeChange("subPort")}
                          label="SubPort"
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                        {scopeAllowedSubPorts.map((sp) => (
                          <MenuItem key={sp} value={String(sp)}>
                            {sp}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {scopeForm.scope === "port" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Port"
                      type="number"
                      fullWidth
                      value={scopeForm.port}
                      onChange={handleScopeChange("port")}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <ChipInput
                    label="Safe Numbers"
                    fullWidth
                    value={scopeForm.safeNumbers}
                    onAdd={handleAddScopedSafeNumber}
                    onDelete={(chip, index) =>
                      setScopeForm((prev) => ({
                        ...prev,
                        safeNumbers: prev.safeNumbers.filter((_, i) => i !== index),
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Mode</InputLabel>
                    <Select
                      value={scopeForm.mode}
                      onChange={handleScopeChange("mode")}
                      label="Mode"
                    >
                      <MenuItem value="">Inherit</MenuItem>
                      <MenuItem value="skip">Skip</MenuItem>
                      <MenuItem value="reroute">Reroute</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Limit"
                  type="number"
                  fullWidth
                  value={scopeForm.limit}
                  onChange={handleScopeChange("limit")}
                  placeholder="Inherit"
                  disabled={!scopeForm.mode}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Carriers</InputLabel>
                  <Select
                    multiple
                    value={scopeForm.carriers}
                    onChange={handleScopeCarriersChange}
                    renderValue={(selected) =>
                      selected.length
                        ? selected
                            .map((value) => carrierLabelMap[value] || value)
                            .join(", ")
                        : ""
                    }
                    disabled={!scopeForm.mode}
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    }}
                  >
                    {carrierOptions.map((carrier) => (
                      <MenuItem
                        key={carrier.value}
                        value={carrier.value}
                        disabled={
                          carrier.value !== "ALL" &&
                          scopeForm.carriers.includes("ALL")
                        }
                      >
                        <Checkbox
                          checked={scopeForm.carriers.indexOf(carrier.value) > -1}
                        />
                        <ListItemText primary={carrier.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={scopeForm.enabled}
                        onChange={(e) =>
                          setScopeForm((prev) => ({
                            ...prev,
                            enabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Enabled"
                  />
                </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!scopeForm.mode}>
                  <InputLabel>Fallback</InputLabel>
                  <Select
                    value={scopeForm.fallback}
                    onChange={handleScopeChange("fallback")}
                    label="Fallback"
                  >
                    <MenuItem value="">Inherit</MenuItem>
                    <MenuItem value="backwash">Backwash</MenuItem>
                    <MenuItem value="dontSend">DontSend</MenuItem>
                  </Select>
                </FormControl>
                </Grid>
              </Grid>
              <Box className={classes.actionsRow}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveScope}
                  disabled={loading || !isScopeDirty}
                >
                  {editingScope ? "Update Scoped Config" : "Save Scoped Config"}
                </Button>
                {editingScope && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={handleCancelEdit}
                    style={{ marginLeft: 8 }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography className={classes.sectionTitle}>
                Scoped Configs
              </Typography>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={handleDeleteAllScopedConfigs}
                  style={{ marginRight: 8 }}
                >
                  Delete All Scoped Configs
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={handleDeleteSelectedConfigs}
                  disabled={!selectedConfigs.length}
                >
                  Delete Selected
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Scope</InputLabel>
                    <Select
                      value={configFilters.scope}
                      onChange={handleFilterChange("scope")}
                      label="Scope"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="box">Box</MenuItem>
                      <MenuItem value="subport">SubPort</MenuItem>
                      <MenuItem value="port">Port</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Box</InputLabel>
                    <Select
                      value={configFilters.box}
                      onChange={handleFilterChange("box")}
                      label="Box"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {simBoxesUnifiedSet.map((box) => (
                        <MenuItem key={box} value={box}>
                          {box}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>SubPort</InputLabel>
                    <Select
                      value={configFilters.subPort}
                      onChange={handleFilterChange("subPort")}
                      label="SubPort"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((sp) => (
                        <MenuItem key={sp} value={String(sp)}>
                          {sp}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    variant="outlined"
                    label="Port"
                    fullWidth
                    value={configFilters.port}
                    onChange={handleFilterChange("port")}
                  />
                </Grid>
              </Grid>
              <Divider />
              {configTotal > 0 && (
                <Pagination
                  showHead="true"
                  pages={Math.max(1, Math.ceil(configTotal / Number(configPage.limit)))}
                  totalCounts={configTotal}
                  handlePage={handleConfigPageChange}
                  handleLimit={handleConfigLimitChange}
                  currentLimit={configPage.limit}
                  currentPage={Number(configPage.pageNumber)}
                />
              )}
              <Box className={classes.tableWrap} mt={2}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" className={classes.tableHeadCell}>
                        <Checkbox
                          color="primary"
                          indeterminate={
                            selectedConfigs.length > 0 &&
                            selectedConfigs.length < configs.length
                          }
                          checked={
                            configs.length > 0 &&
                            selectedConfigs.length === configs.length
                          }
                          onChange={handleToggleAllConfigs}
                        />
                      </TableCell>
                      <TableCell className={classes.tableHeadCell}>Scope</TableCell>
                      <TableCell className={classes.tableHeadCell}>Box</TableCell>
                      <TableCell className={classes.tableHeadCell}>SubPort</TableCell>
                      <TableCell className={classes.tableHeadCell}>Port</TableCell>
                      <TableCell className={classes.tableHeadCell}>Mode</TableCell>
                      <TableCell className={classes.tableHeadCell}>Limit</TableCell>
                      <TableCell className={classes.tableHeadCell}>Carriers</TableCell>
                      <TableCell className={classes.tableHeadCell}>Safe Numbers</TableCell>
                      <TableCell className={classes.tableHeadCell}>Enabled</TableCell>
                      <TableCell className={classes.tableHeadCell}>Fallback</TableCell>
                      <TableCell className={classes.tableHeadCell} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.map((cfg) => (
                      <TableRow key={getConfigKey(cfg)} className={classes.tableRow}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={selectedConfigs.includes(getConfigKey(cfg))}
                            onChange={() => handleToggleConfig(cfg)}
                          />
                        </TableCell>
                        <TableCell>{scopeLabelMap[cfg.scope] || titleCase(cfg.scope)}</TableCell>
                        <TableCell>{cfg.box || "-"}</TableCell>
                        <TableCell>{cfg.subPort || "-"}</TableCell>
                        <TableCell>{cfg.port || "-"}</TableCell>
                        <TableCell>
                          {cfg.mode
                            ? modeLabelMap[cfg.mode] || titleCase(cfg.mode)
                            : "-"}
                        </TableCell>
                        <TableCell>{formatLimitValue(cfg.limit)}</TableCell>
                        <TableCell>
                          {cfg.carriers && cfg.carriers.length
                            ? cfg.carriers
                                .map((value) => carrierLabelMap[value] || titleCase(value))
                                .join(", ")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {(cfg.safeNumbers || []).join(", ") || "-"}
                        </TableCell>
                        <TableCell>
                          {cfg.enabled === false ? "No" : "Yes"}
                        </TableCell>
                        <TableCell>
                          {cfg.fallback
                            ? fallbackLabelMap[cfg.fallback] ||
                              titleCase(cfg.fallback)
                            : "-"}
                        </TableCell>
                      <TableCell className={classes.actionCell}>
                        <Button
                          color="primary"
                          size="small"
                          variant="outlined"
                          onClick={() => handleEditScope(cfg)}
                          style={{ marginRight: 8 }}
                        >
                          Edit
                        </Button>
                        <Button
                          color="secondary"
                          size="small"
                          variant="outlined"
                          onClick={() => handleDeleteScope(cfg)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!configs.length && (
                      <TableRow>
                        <TableCell colSpan={12} align="center" className={classes.emptyCell}>
                          No scoped configs
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              {configTotal > 0 && (
                <Pagination
                  showHead="false"
                  pages={Math.max(1, Math.ceil(configTotal / Number(configPage.limit)))}
                  totalCounts={configTotal}
                  handlePage={handleConfigPageChange}
                  handleLimit={handleConfigLimitChange}
                  currentLimit={configPage.limit}
                  currentPage={Number(configPage.pageNumber)}
                />
              )}
            </Paper>
          </Grid>

        </Grid>
      )}

      {tabIndex === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography className={classes.sectionTitle}>
                CSV Import (Scoped Configs)
              </Typography>
              <Paper className={classes.paper} style={{ marginBottom: 16, backgroundColor: "#f8fafc" }}>
                <Typography className={classes.sectionTitle}>
                  Custom Sample Generator
                </Typography>
                <Typography variant="body2" className={classes.subtleText} style={{ marginBottom: 16 }}>
                  Generate a scoped CSV template by box range. Scope and routing columns are prefilled, and
                  you can just enter `safeNumbers` in the downloaded file.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Scope</InputLabel>
                      <Select
                        value={sampleConfig.scope}
                        onChange={handleCustomSampleChange("scope")}
                      >
                        <MenuItem value="box">Box</MenuItem>
                        <MenuItem value="subport">SubPort</MenuItem>
                        <MenuItem value="port">Port</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>From Box</InputLabel>
                      <Select
                        value={sampleConfig.fromBox}
                        onChange={handleCustomSampleChange("fromBox")}
                      >
                        {simBoxesUnifiedSet.map((box) => (
                          <MenuItem key={`sample-from-${box}`} value={box}>
                            {box}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>To Box</InputLabel>
                      <Select
                        value={sampleConfig.toBox}
                        onChange={handleCustomSampleChange("toBox")}
                      >
                        {simBoxesUnifiedSet.map((box) => (
                          <MenuItem key={`sample-to-${box}`} value={box}>
                            {box}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {sampleConfig.scope !== "box" && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <InputLabel>From SubPort</InputLabel>
                          <Select
                            value={sampleConfig.fromSubPort}
                            onChange={handleCustomSampleChange("fromSubPort")}
                          >
                            {sampleFromAllowedSubPorts.map((subPort) => (
                              <MenuItem key={`sample-from-sub-${subPort}`} value={String(subPort)}>
                                {subPort}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                          <InputLabel>To SubPort</InputLabel>
                          <Select
                            value={sampleConfig.toSubPort}
                            onChange={handleCustomSampleChange("toSubPort")}
                          >
                            {sampleToAllowedSubPorts.map((subPort) => (
                              <MenuItem key={`sample-to-sub-${subPort}`} value={String(subPort)}>
                                {subPort}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  {sampleConfig.scope === "port" && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="From Port"
                          type="number"
                          fullWidth
                          value={sampleConfig.fromPort}
                          onChange={handleCustomSampleChange("fromPort")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="To Port"
                          type="number"
                          fullWidth
                          value={sampleConfig.toPort}
                          onChange={handleCustomSampleChange("toPort")}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Mode</InputLabel>
                      <Select
                        value={sampleConfig.mode}
                        onChange={handleCustomSampleChange("mode")}
                      >
                        <MenuItem value="">Inherit</MenuItem>
                        <MenuItem value="skip">Skip</MenuItem>
                        <MenuItem value="reroute">Reroute</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Limit"
                      type="number"
                      fullWidth
                      value={sampleConfig.limit}
                      onChange={handleCustomSampleChange("limit")}
                      disabled={!sampleConfig.mode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth disabled={!sampleConfig.mode}>
                      <InputLabel>Fallback</InputLabel>
                      <Select
                        value={sampleConfig.fallback}
                        onChange={handleCustomSampleChange("fallback")}
                      >
                        <MenuItem value="backwash">Backwash</MenuItem>
                        <MenuItem value="dontSend">DontSend</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth disabled={!sampleConfig.mode}>
                      <InputLabel>Carriers</InputLabel>
                      <Select
                        multiple
                        value={sampleConfig.carriers}
                        onChange={handleSampleCarriersChange}
                        renderValue={(selected) =>
                          selected.map((value) => carrierLabelMap[value] || value).join(", ")
                        }
                        MenuProps={{
                          getContentAnchorEl: null,
                          anchorOrigin: { vertical: "bottom", horizontal: "left" },
                          transformOrigin: { vertical: "top", horizontal: "left" },
                        }}
                      >
                        {carrierOptions.map((carrier) => (
                          <MenuItem
                            key={`sample-carrier-${carrier.value}`}
                            value={carrier.value}
                            disabled={
                              carrier.value !== "ALL" &&
                              sampleConfig.carriers.includes("ALL")
                            }
                          >
                            <Checkbox
                              checked={sampleConfig.carriers.indexOf(carrier.value) > -1}
                            />
                            <ListItemText primary={carrier.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Box className={classes.actionsRow}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleCustomSampleDownload}
                  >
                    Download Custom Sample
                  </Button>
                </Box>
              </Paper>
              <Box mb={2}>
                <input type="file" accept=".csv" onChange={handleCsvUpload} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSampleDownload}
                  style={{ marginLeft: 12 }}
                >
                  Download Sample
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    resetCsvImportState();
                    setStatus(null);
                  }}
                  style={{ marginLeft: 12 }}
                >
                  Clear Import
                </Button>
              </Box>
              {csvHeaders.length > 0 && (
                <>
                  <Grid container spacing={2}>
                    {Object.keys(csvMapping).map((field) => (
                      <Grid item xs={12} sm={4} key={field}>
                        <FormControl fullWidth>
                          <InputLabel>{titleCase(field)}</InputLabel>
                          <Select
                            value={csvMapping[field]}
                            onChange={handleCsvMappingChange(field)}
                          >
                            <MenuItem value="">Ignore</MenuItem>
                            {csvHeaders.map((header) => (
                              <MenuItem key={`${field}-${header}`} value={header}>
                                {header}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    ))}
                  </Grid>
                  <Box mt={2}>
                    <FormControl>
                      <InputLabel>Import Mode</InputLabel>
                      <Select
                        value={csvMode}
                        onChange={(event) => setCsvMode(event.target.value)}
                      >
                        <MenuItem value="skip">Skip Invalid Rows</MenuItem>
                        <MenuItem value="strict">Block On Errors</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box className={classes.actionsRow}>
                    <Button variant="outlined" onClick={handleCsvPreview}>
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleCsvImport}
                      style={{ marginLeft: 8 }}
                    >
                      Import
                    </Button>
                  </Box>
                  {csvPreview.errors.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2">
                        Errors (first 5)
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.tableHeadCell}>Row</TableCell>
                            <TableCell className={classes.tableHeadCell}>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {csvPreview.errors.slice(0, 5).map((err, idx) => (
                            <TableRow key={`csv-err-${idx}`} className={classes.tableRow}>
                              <TableCell>{err.row}</TableCell>
                              <TableCell className={classes.subtleText}>{err.error}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                  {csvPreview.configs.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2">
                        Preview (first 5)
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.tableHeadCell}>Scope</TableCell>
                            <TableCell className={classes.tableHeadCell}>Box</TableCell>
                            <TableCell className={classes.tableHeadCell}>SubPort</TableCell>
                            <TableCell className={classes.tableHeadCell}>Port</TableCell>
                            <TableCell className={classes.tableHeadCell}>Mode</TableCell>
                            <TableCell className={classes.tableHeadCell}>Limit</TableCell>
                            <TableCell className={classes.tableHeadCell}>Carriers</TableCell>
                            <TableCell className={classes.tableHeadCell}>Safe Numbers</TableCell>
                            <TableCell className={classes.tableHeadCell}>Enabled</TableCell>
                            <TableCell className={classes.tableHeadCell}>Fallback</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {csvPreview.configs.slice(0, 5).map((row, idx) => (
                            <TableRow key={`csv-prev-${idx}`} className={classes.tableRow}>
                              <TableCell>{row.scope || "-"}</TableCell>
                              <TableCell>{row.box || "-"}</TableCell>
                              <TableCell>{row.subPort || "-"}</TableCell>
                              <TableCell>{row.port || "-"}</TableCell>
                              <TableCell>{row.mode || "-"}</TableCell>
                              <TableCell>{formatLimitValue(row.limit)}</TableCell>
                              <TableCell>{(row.carriers || []).join(", ") || "-"}</TableCell>
                              <TableCell>{(row.safeNumbers || []).join(", ") || "-"}</TableCell>
                              <TableCell>
                                {row.enabled === undefined
                                  ? "-"
                                  : row.enabled
                                  ? "Yes"
                                  : "No"}
                              </TableCell>
                              <TableCell>{row.fallback || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabIndex === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography className={classes.sectionTitle}>
                Carrier Control Events (Latest)
              </Typography>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={exportEventsCsv}
                  style={{ marginRight: 8 }}
                >
                  Download CSV
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={fetchEvents}
                  className={classes.refreshButton}
                >
                  Refresh Events
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Box</InputLabel>
                    <Select
                      value={eventFilters.box}
                      onChange={handleEventFilterChange("box")}
                      label="Box"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {simBoxesUnifiedSet.map((box) => (
                        <MenuItem key={`evt-box-${box}`} value={box}>
                          {box}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>SubPort</InputLabel>
                    <Select
                      value={eventFilters.subPort}
                      onChange={handleEventFilterChange("subPort")}
                      label="SubPort"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((sp) => (
                        <MenuItem key={`evt-sp-${sp}`} value={String(sp)}>
                          {sp}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    variant="outlined"
                    label="Port"
                    fullWidth
                    value={eventFilters.port}
                    onChange={handleEventFilterChange("port")}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={eventFilters.type}
                      onChange={handleEventFilterChange("type")}
                      label="Type"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="CSB">CSB</MenuItem>
                      <MenuItem value="ICR">ICR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={eventFilters.action}
                      onChange={handleEventFilterChange("action")}
                      label="Action"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="skip">Skip</MenuItem>
                      <MenuItem value="reroute">Reroute</MenuItem>
                      <MenuItem value="backwash">Backwash</MenuItem>
                      <MenuItem value="dontSend">DontSend</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Divider />
              {eventTotal > 0 && (
                <Pagination
                  showHead="true"
                  pages={Math.max(1, Math.ceil(eventTotal / Number(eventPage.limit)))}
                  totalCounts={eventTotal}
                  handlePage={handleEventPageChange}
                  handleLimit={handleEventLimitChange}
                  currentLimit={eventPage.limit}
                  currentPage={Number(eventPage.pageNumber)}
                />
              )}
              <Box className={classes.tableWrap} mt={2}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Time</TableCell>
                      <TableCell className={classes.tableHeadCell}>Type</TableCell>
                      <TableCell className={classes.tableHeadCell}>Box</TableCell>
                      <TableCell className={classes.tableHeadCell}>SubPort</TableCell>
                      <TableCell className={classes.tableHeadCell}>Port</TableCell>
                      <TableCell className={classes.tableHeadCell}>Carrier</TableCell>
                      <TableCell className={classes.tableHeadCell}>Action</TableCell>
                      <TableCell className={classes.tableHeadCell}>Phone</TableCell>
                      <TableCell className={classes.tableHeadCell}>Reroute</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((evt, idx) => (
                      <TableRow key={`${evt._id || idx}`} className={classes.tableRow}>
                        <TableCell>
                          {evt.createdAt
                            ? moment(evt.createdAt).format("YYYY-MM-DD HH:mm:ss")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {eventTypeLabelMap[getEventType(evt)] || getEventType(evt) || "-"}
                        </TableCell>
                        <TableCell>{evt.box || "-"}</TableCell>
                        <TableCell>{evt.subPort || "-"}</TableCell>
                        <TableCell>{evt.port || "-"}</TableCell>
                        <TableCell>{evt.carrier || "-"}</TableCell>
                        <TableCell>{evt.action || "-"}</TableCell>
                        <TableCell>{evt.phone || "-"}</TableCell>
                        <TableCell>{evt.reroutePhone || "-"}</TableCell>
                      </TableRow>
                    ))}
                    {!events.length && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" className={classes.emptyCell}>
                          No carrier control events
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              {eventTotal > 0 && (
                <Pagination
                  showHead="false"
                  pages={Math.max(1, Math.ceil(eventTotal / Number(eventPage.limit)))}
                  totalCounts={eventTotal}
                  handlePage={handleEventPageChange}
                  handleLimit={handleEventLimitChange}
                  currentLimit={eventPage.limit}
                  currentPage={Number(eventPage.pageNumber)}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabIndex === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography className={classes.sectionTitle}>
                Carrier Control Summary (By Port)
              </Typography>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={exportSummaryCsv}
                  style={{ marginRight: 8 }}
                >
                  Download CSV
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={fetchSummary}
                  className={classes.refreshButton}
                >
                  Refresh Summary
                </Button>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Box</InputLabel>
                    <Select
                      value={summaryFilters.box}
                      onChange={handleSummaryFilterChange("box")}
                      label="Box"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {simBoxesUnifiedSet.map((box) => (
                        <MenuItem key={`sum-box-${box}`} value={box}>
                          {box}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>SubPort</InputLabel>
                    <Select
                      value={summaryFilters.subPort}
                      onChange={handleSummaryFilterChange("subPort")}
                      label="SubPort"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((sp) => (
                        <MenuItem key={`sum-sp-${sp}`} value={String(sp)}>
                          {sp}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    variant="outlined"
                    label="Port"
                    fullWidth
                    value={summaryFilters.port}
                    onChange={handleSummaryFilterChange("port")}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={summaryFilters.type}
                      onChange={handleSummaryFilterChange("type")}
                      label="Type"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="CSB">CSB</MenuItem>
                      <MenuItem value="ICR">ICR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Divider />
              {summaryTotal > 0 && (
                <Pagination
                  showHead="true"
                  pages={Math.max(1, Math.ceil(summaryTotal / Number(summaryPage.limit)))}
                  totalCounts={summaryTotal}
                  handlePage={handleSummaryPageChange}
                  handleLimit={handleSummaryLimitChange}
                  currentLimit={summaryPage.limit}
                  currentPage={Number(summaryPage.pageNumber)}
                />
              )}
              <Box className={classes.tableWrap} mt={2}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Box</TableCell>
                      <TableCell className={classes.tableHeadCell}>SubPort</TableCell>
                      <TableCell className={classes.tableHeadCell}>Port</TableCell>
                      <TableCell className={classes.tableHeadCell}>Type</TableCell>
                      <TableCell className={classes.tableHeadCell}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.map((row, idx) => (
                      <TableRow key={`${row._id?.box || idx}-${idx}`} className={classes.tableRow}>
                        <TableCell>{row._id?.box || "-"}</TableCell>
                        <TableCell>{row._id?.subPort || "-"}</TableCell>
                        <TableCell>{row._id?.port || "-"}</TableCell>
                        <TableCell>
                          {eventTypeLabelMap[row._id?.type] || row._id?.type || "-"}
                        </TableCell>
                        <TableCell>{row.count || 0}</TableCell>
                      </TableRow>
                    ))}
                    {!summary.length && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" className={classes.emptyCell}>
                          No carrier control summary
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              {summaryTotal > 0 && (
                <Pagination
                  showHead="false"
                  pages={Math.max(1, Math.ceil(summaryTotal / Number(summaryPage.limit)))}
                  totalCounts={summaryTotal}
                  handlePage={handleSummaryPageChange}
                  handleLimit={handleSummaryLimitChange}
                  currentLimit={summaryPage.limit}
                  currentPage={Number(summaryPage.pageNumber)}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      </Container>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onConfirm={handleDoConfirm}
        onCancel={handleCloseConfirm}
      />
    </React.Fragment>
  );
};

export default CsbManager;
